import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

const BlogPage = () => {
  useSEO({
    title: "Blog - Digital Business Card Tips & Insights | AM CARDMATE",
    description: "Stay updated with the latest trends in digital networking, NFC technology, and professional tips for digital business cards.",
    image: "/assets/blog.jpg",
    keywords: "blog, digital business cards, NFC technology, networking tips, business advice"
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('');
  const [hoveredPost, setHoveredPost] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const categories = [
    { id: 'all', name: 'All Posts', count: 9 },
    { id: 'technology', name: 'Technology', count: 4 },
    { id: 'guides', name: 'How-To Guides', count: 3 },
    { id: 'business', name: 'Business Tips', count: 2 }
  ];

  const blogPosts = [
    {
      id: 1,
      slug: 'what-is-nfc-technology',
      title: "What is NFC Technology? A Complete Guide",
      excerpt: "Discover everything you need to know about Near Field Communication (NFC) technology, how it works, and why it's revolutionizing digital business cards.",
      category: 'technology',
      author: "Alina Macovei",
      date: "June 10, 2025",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=600&fit=crop",
      featured: true,
      tags: ['NFC', 'Technology', 'Innovation'],
      gradient: 'from-blue-400 to-blue-600'
    },
    {
      id: 2,
      slug: 'how-nfc-business-cards-work',
      title: "How NFC Business Cards Work in AM CARDMATE",
      excerpt: "Step-by-step explanation of how our NFC cards instantly share your digital profile with a simple tap. No app needed for recipients!",
      category: 'guides',
      author: "Tech Team",
      date: "June 8, 2025",
      readTime: "4 min read",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      tags: ['Tutorial', 'NFC', 'Features'],
      gradient: 'from-purple-400 to-pink-600'
    },
    {
      id: 3,
      slug: 'nfc-vs-qr-codes',
      title: "NFC vs QR Codes: Which is Better for Digital Business Cards?",
      excerpt: "Compare the pros and cons of NFC and QR code technologies for digital business cards. Find out which solution works best for your networking needs.",
      category: 'technology',
      author: "Alina Macovei",
      date: "June 5, 2025",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1595079676339-1534801ad6cf?w=800&h=600&fit=crop",
      tags: ['Comparison', 'Technology', 'QR Codes'],
      gradient: 'from-green-400 to-teal-600'
    },
    {
      id: 4,
      slug: 'environmental-impact-digital-cards',
      title: "The Environmental Impact of Digital Business Cards",
      excerpt: "Learn how switching to digital business cards can save thousands of trees and reduce your carbon footprint. Join the eco-friendly networking revolution!",
      category: 'business',
      author: "Green Team",
      date: "June 3, 2025",
      readTime: "3 min read",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=600&fit=crop",
      tags: ['Sustainability', 'Environment', 'Green'],
      gradient: 'from-green-500 to-green-700'
    },
    {
      id: 5,
      slug: 'creative-ways-use-nfc-card',
      title: "10 Creative Ways to Use Your NFC Business Card",
      excerpt: "Beyond basic contact sharing: discover innovative ways professionals are using NFC cards to stand out and create memorable networking experiences.",
      category: 'business',
      author: "Marketing Team",
      date: "May 30, 2025",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=600&fit=crop",
      tags: ['Tips', 'Networking', 'Creative'],
      gradient: 'from-orange-400 to-red-600'
    },
    {
      id: 6,
      slug: 'setting-up-digital-business-card',
      title: "Setting Up Your First Digital Business Card: Step-by-Step Guide",
      excerpt: "Complete walkthrough on creating your professional digital presence with AM CARDMATE. From profile setup to sharing your first card.",
      category: 'guides',
      author: "Support Team",
      date: "May 28, 2025",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
      tags: ['Tutorial', 'Getting Started', 'Guide'],
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      id: 7,
      slug: 'future-of-networking',
      title: "The Future of Networking: Why Paper Cards Are Becoming Obsolete",
      excerpt: "Explore the digital transformation of professional networking and why forward-thinking businesses are ditching paper for smart digital solutions.",
      category: 'technology',
      author: "Alina Macovei",
      date: "May 25, 2025",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop",
      tags: ['Future', 'Trends', 'Digital'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 8,
      slug: 'track-networking-roi',
      title: "How to Track Your Networking ROI with Digital Analytics",
      excerpt: "Master the art of data-driven networking. Learn to use AM CARDMATE's analytics to measure and improve your professional connections.",
      category: 'guides',
      author: "Analytics Team",
      date: "May 22, 2025",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      tags: ['Analytics', 'ROI', 'Data'],
      gradient: 'from-cyan-400 to-blue-600'
    },
    {
      id: 9,
      slug: 'nfc-security',
      title: "NFC Security: How Safe Are Digital Business Cards?",
      excerpt: "Deep dive into the security features of NFC technology and how AM CARDMATE protects your professional information with bank-level encryption.",
      category: 'technology',
      author: "Security Team",
      date: "May 20, 2025",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&h=600&fit=crop",
      tags: ['Security', 'Privacy', 'Technology'],
      gradient: 'from-red-400 to-red-600'
    }
  ];

  useEffect(() => {
    const blogSchema = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "AM CARDMATE Blog",
      "description": "Digital business card tips and insights",
      "url": "https://amcardmate.com/blog",
      "blogPost": blogPosts.map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.image,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "datePublished": new Date(post.date).toISOString(),
        "url": `https://amcardmate.com/blog/${post.slug}`
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(blogSchema);
    document.head.appendChild(script);
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');

    try {
      const response = await fetch('https://formspree.io/f/xldnlevd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail,
          message: `New Blog Newsletter Subscription:\n\nEmail: ${newsletterEmail}\nPage: Blog Newsletter`
        }),
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterMessage('Successfully subscribed! Check your inbox for updates.');
        setNewsletterEmail('');

        setTimeout(() => {
          setNewsletterStatus('idle');
          setNewsletterMessage('');
        }, 5000);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterMessage('Something went wrong. Please try again.');

      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMessage('');
      }, 5000);
    }
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesTag = selectedTag === '' || post.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase());
    return matchesCategory && matchesTag;
  });

  const featuredPost = blogPosts.find(post => post.featured);

  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    setSelectedCategory('all');
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedTag('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-32 lg:pt-40 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-b from-white via-blue-50/20 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 sm:w-96 md:w-[800px] h-64 sm:h-96 md:h-[800px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-48 sm:w-72 md:w-[600px] h-48 sm:h-72 md:h-[600px] bg-gradient-to-tr from-purple-200/20 via-pink-200/20 to-transparent rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="badge badge-blue inline-flex mb-4 sm:mb-6">
              <FontAwesomeIcon icon="book-open" />
              <span>AM CARDMATE Blog</span>
            </div>

            <h1 className="text-h1 mb-4 sm:mb-6 text-dark">
              Insights & Guides for
              <span className="block text-gradient-blue-pink mt-1 sm:mt-2">Digital Networking</span>
            </h1>

            <p className="text-body-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              Expert tips, technology insights, and guides to help you master the art of modern professional networking with NFC business cards.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post Section */}
      {featuredPost && (
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 bg-gradient-to-b from-white to-gray-50/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-6 sm:mb-8 lg:mb-12">
                <div className="badge badge-pink inline-flex mb-3 sm:mb-4">
                  <FontAwesomeIcon icon="fire" />
                  <span>Featured Article</span>
                </div>
                <h2 className="text-h3 text-dark">Don't Miss This</h2>
              </div>

              <Link to={`/blog/${featuredPost.slug}`}>
                <motion.div
                  className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden group cursor-pointer"
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative h-48 sm:h-64 md:h-72 lg:h-auto order-2 lg:order-1">
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${featuredPost.gradient} opacity-20 group-hover:opacity-30 transition-opacity`} />
                    </div>

                    <div className="p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12 order-1 lg:order-2 flex flex-col justify-center">
                      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                        {featuredPost.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary-blue/10 text-primary-blue text-label"
                          >
                            <FontAwesomeIcon icon="tag" className="text-xs" />
                            <span>{tag}</span>
                          </span>
                        ))}
                      </div>

                      <h3 className="text-h4 text-dark mb-3 sm:mb-4 group-hover:text-primary-blue transition-colors leading-tight">
                        {featuredPost.title}
                      </h3>

                      <p className="text-body text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                        {featuredPost.excerpt}
                      </p>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-body-sm text-gray-600">
                          <span className="flex items-center gap-1.5 sm:gap-2">
                            <FontAwesomeIcon icon="user" className="text-primary-blue flex-shrink-0" />
                            <span className="truncate">{featuredPost.author}</span>
                          </span>
                          <span className="flex items-center gap-1.5 sm:gap-2">
                            <FontAwesomeIcon icon="calendar" className="text-primary-blue flex-shrink-0" />
                            <span className="hidden sm:inline">{featuredPost.date}</span>
                            <span className="sm:hidden">{featuredPost.date.split(' ').slice(0, 2).join(' ')}</span>
                          </span>
                          <span className="flex items-center gap-1.5 sm:gap-2">
                            <FontAwesomeIcon icon="clock" className="text-primary-blue flex-shrink-0" />
                            <span>{featuredPost.readTime}</span>
                          </span>
                        </div>

                        <motion.div
                          className="btn btn-primary"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span>Read More</span>
                          <FontAwesomeIcon icon="arrow-right" />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50/30 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                {/* Categories */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg mb-6">
                  <h3 className="text-h5 text-dark mb-3 sm:mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="filter" className="text-primary-blue" />
                    Categories
                  </h3>

                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSelectedTag('');
                        }}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-left transition-all text-body-sm ${
                          selectedCategory === category.id && selectedTag === ''
                            ? 'bg-gradient-to-r from-primary-blue to-accent-purple text-white'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{category.name}</span>
                          <span className={`text-caption ${
                            selectedCategory === category.id && selectedTag === '' ? 'text-white/80' : 'text-gray-500'
                          }`}>
                            {category.count}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg">
                  <h3 className="text-h5 text-dark mb-3 sm:mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="tags" className="text-accent-purple" />
                    Popular Tags
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {['NFC', 'Technology', 'Tutorial', 'Analytics', 'Security', 'Business', 'Networking'].map((tag) => (
                      <motion.button
                        key={tag}
                        onClick={() => handleTagClick(tag)}
                        className={`px-2 sm:px-3 py-1 text-btn-sm rounded-full transition-all ${
                          selectedTag === tag
                            ? 'bg-gradient-to-r from-primary-blue to-accent-purple text-white'
                            : 'bg-gray-100 hover:bg-gradient-to-r hover:from-primary-blue hover:to-accent-purple hover:text-white text-gray-600'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {tag}
                      </motion.button>
                    ))}
                  </div>

                  {(selectedCategory !== 'all' || selectedTag !== '') && (
                    <button
                      onClick={clearFilters}
                      className="w-full mt-4 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-body-sm transition-colors"
                    >
                      <FontAwesomeIcon icon="times" className="mr-2" />
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* Posts Grid */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-2">
                  <h2 className="text-h4 text-dark">
                    {selectedTag ? `Tagged: ${selectedTag}` : selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
                  </h2>
                  <span className="text-body-sm text-gray-600">
                    {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedCategory + selectedTag}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
                  >
                    {filteredPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="block h-full"
                      >
                        <motion.article
                          className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden group cursor-pointer h-full"
                          whileHover={{ y: -8 }}
                          onHoverStart={() => setHoveredPost(post.id)}
                          onHoverEnd={() => setHoveredPost(null)}
                        >
                          <div className="relative h-36 sm:h-48 overflow-hidden">
                            <img
                              src={post.image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className={`absolute inset-0 bg-gradient-to-br ${post.gradient} opacity-20 group-hover:opacity-30 transition-opacity`} />

                            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-white/90 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full text-caption font-medium text-gray-700">
                              <FontAwesomeIcon icon="clock" className="mr-1 text-xs" />
                              {post.readTime}
                            </div>
                          </div>

                          <div className="p-4 sm:p-6">
                            <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                              {post.tags.slice(0, 2).map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 text-caption font-medium text-gray-600 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>

                            <h3 className="text-card-title text-dark mb-2 sm:mb-3 group-hover:text-primary-blue transition-colors line-clamp-2">
                              {post.title}
                            </h3>

                            <p className="text-card-body mb-3 sm:mb-4 line-clamp-3">
                              {post.excerpt}
                            </p>

                            <div className="flex items-center justify-between text-body-sm text-gray-600">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon="user" className="text-xs text-primary-blue" />
                                  <span className="hidden sm:inline">{post.author}</span>
                                  <span className="sm:hidden">{post.author.split(' ')[0]}</span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon="calendar" className="text-xs text-primary-blue" />
                                  <span className="hidden sm:inline">{post.date}</span>
                                  <span className="sm:hidden">{post.date.split(',')[0]}</span>
                                </span>
                              </div>

                              <FontAwesomeIcon
                                icon="arrow-right"
                                className={`text-primary-blue transition-transform ${
                                  hoveredPost === post.id ? 'translate-x-1' : ''
                                }`}
                              />
                            </div>
                          </div>
                        </motion.article>
                      </Link>
                    ))}
                  </motion.div>
                </AnimatePresence>

                {filteredPosts.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FontAwesomeIcon icon="search" className="text-xl sm:text-2xl text-gray-400" />
                    </div>
                    <p className="text-body text-gray-600 mb-4">
                      {selectedTag ? `No articles found with tag "${selectedTag}"` : 'No articles found in this category.'}
                    </p>
                    <button
                      onClick={clearFilters}
                      className="btn btn-outline"
                    >
                      View All Articles
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-h2 mb-3 sm:mb-4 text-white leading-tight">
              Stay Updated with Digital
              <span className="block bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                Networking Trends
              </span>
            </h2>
            <p className="text-body-lg text-white/90 mb-6 sm:mb-8 px-4">
              Get the latest insights on NFC technology and digital business cards delivered to your inbox.
            </p>

            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto px-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <input
                  type="email"
                  name="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 sm:px-6 py-3 rounded-full text-gray-900 focus:outline-none shadow-lg text-body"
                  required
                  disabled={newsletterStatus === 'submitting'}
                />
                <motion.button
                  type="submit"
                  disabled={newsletterStatus === 'submitting'}
                  className="px-6 sm:px-8 py-3 bg-white text-[#4DA2FF] text-btn font-semibold rounded-full hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: newsletterStatus === 'submitting' ? 1 : 1.05 }}
                  whileTap={{ scale: newsletterStatus === 'submitting' ? 1 : 0.95 }}
                >
                  {newsletterStatus === 'submitting' ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="mr-2 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </motion.button>
              </div>

              {newsletterStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white text-body"
                >
                  <FontAwesomeIcon icon="check-circle" className="mr-2" />
                  {newsletterMessage}
                </motion.div>
              )}

              {newsletterStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-red-500/20 backdrop-blur-sm border border-red-300/30 rounded-xl text-white text-body"
                >
                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                  {newsletterMessage}
                </motion.div>
              )}
            </form>

            <p className="text-body-sm text-white/70 mt-3 sm:mt-4">
              <FontAwesomeIcon icon="users" className="mr-2" />
              Join countless professionals. No spam, unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="badge badge-purple inline-flex mb-3 sm:mb-4">
              <FontAwesomeIcon icon="lightbulb" />
              <span>Resources</span>
            </div>
            <h2 className="text-h3 text-dark">Explore More Resources</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            <motion.a
              href="/help"
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all group"
              whileHover={{ y: -5 }}
            >
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white mb-3 sm:mb-4">
                <FontAwesomeIcon icon="question-circle" className="text-xl sm:text-2xl" />
              </div>
              <h3 className="text-card-title text-dark mb-2 group-hover:text-primary-blue transition-colors">Help Center</h3>
              <p className="text-card-body">Find answers to common questions about our platform.</p>
            </motion.a>

            <motion.a
              href="/contact"
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all group"
              whileHover={{ y: -5 }}
            >
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-purple-400 to-pink-600 rounded-xl flex items-center justify-center text-white mb-3 sm:mb-4">
                <FontAwesomeIcon icon="envelope" className="text-xl sm:text-2xl" />
              </div>
              <h3 className="text-card-title text-dark mb-2 group-hover:text-primary-blue transition-colors">Contact Us</h3>
              <p className="text-card-body">Get in touch with our support team for assistance.</p>
            </motion.a>

            <motion.a
              href="/nfc-cards"
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all group sm:col-span-2 md:col-span-1"
              whileHover={{ y: -5 }}
            >
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-green-400 to-teal-600 rounded-xl flex items-center justify-center text-white mb-3 sm:mb-4">
                <FontAwesomeIcon icon="credit-card" className="text-xl sm:text-2xl" />
              </div>
              <h3 className="text-card-title text-dark mb-2 group-hover:text-primary-blue transition-colors">Shop NFC Cards</h3>
              <p className="text-card-body">Browse our collection of premium NFC business cards.</p>
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;